import { useCallback, useState } from 'react';
import { Star } from 'lucide-react';
import { useIntl } from 'react-intl';

interface ActivityRatingProps {
  value: number;
  onRate: (rating: number) => void;
  /** Feedback already given — show it, but don't let it change. */
  readOnly?: boolean;
}

const STARS = [1, 2, 3, 4, 5];

interface RatingStarProps {
  n: number;
  on: boolean;
  checked: boolean;
  label: string;
  readOnly: boolean;
  onRate: (n: number) => void;
  onHover: (n: number) => void;
  onLeave: () => void;
}

const RatingStar = ({ n, on, checked, label, readOnly, onRate, onHover, onLeave }: RatingStarProps): JSX.Element => {
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onRate(n);
    },
    [onRate, n],
  );
  const handleHover = useCallback(() => onHover(n), [onHover, n]);

  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      aria-label={label}
      // Disabled rather than unmounted, so the rating given stays visible and screen readers still
      // announce which star is checked using the labels they already have.
      disabled={readOnly}
      onClick={handleClick}
      onMouseEnter={handleHover}
      onMouseLeave={onLeave}
      className="flex p-0.5 disabled:cursor-default"
      style={{ color: on ? '#ff9901' : '#d3d8dc' }}
    >
      <Star size={21} strokeWidth={1.5} fill={on ? 'currentColor' : 'none'} />
    </button>
  );
};

/**
 * 5-star rating for a completed, rateable item.
 *
 * Feedback is given once and never edited (the temperature-voting pattern, VIS-1533): once submitted
 * the stars lock rather than inviting a different answer, and the label switches to a thank-you.
 */
export const ActivityRating = ({ value, onRate, readOnly = false }: ActivityRatingProps): JSX.Element => {
  const intl = useIntl();
  const [hover, setHover] = useState(0);
  const rated = value > 0;
  const resetHover = useCallback(() => setHover(0), []);

  return (
    <div className="flex flex-col gap-1" data-testid="activity-rating">
      <span className="text-[11px] font-medium text-system-grey-dark-alt">
        {intl.formatMessage({ id: rated ? 'activity.rating.thanks' : 'activity.rating.prompt' })}
      </span>
      <div
        className="flex items-center gap-0.5"
        role="radiogroup"
        aria-label={intl.formatMessage({ id: 'activity.rating.prompt' })}
      >
        {STARS.map((n) => (
          <RatingStar
            key={n}
            n={n}
            readOnly={readOnly}
            on={(readOnly ? value : hover || value) >= n}
            checked={value === n}
            label={intl.formatMessage({ id: 'activity.rating.star' }, { n })}
            onRate={onRate}
            onHover={setHover}
            onLeave={resetHover}
          />
        ))}
      </div>
    </div>
  );
};
